<template>
  <div class="res-page">
    <!-- Decorative top strip -->
    <div class="res-top-strip"></div>

    <div class="res-container">

      <!-- Header -->
      <div class="res-header">
        <div class="res-header-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
          </svg>
        </div>
        <h1 class="res-header-title">تسجيل حجز جديد</h1>
        <p class="res-header-sub">أكمل البيانات التالية للحجز في مجموعات أ/ طارق عبدالغني عبدالباري</p>
      </div>

      <!-- ══ Step 1: choose academic year ══ -->
      <div class="res-card" v-if="step === 1">
        <div class="res-section" style="border-bottom:none; padding-top:0;">
          <div class="res-section-head">
            <div class="res-section-dot"></div>
            <span>اختر السنة الدراسية</span>
          </div>

          <div v-if="loadingYears" class="res-loading">
            <span class="res-spinner res-spinner--dark"></span>
          </div>

          <div v-else-if="!academicYears.length" class="res-empty">
            لا توجد سنوات دراسية متاحة للحجز حالياً، برجاء المحاولة لاحقاً.
          </div>

          <template v-else>
            <div class="res-year-stage" v-for="stage in stagesWithYears" :key="stage.key">
              <h3 class="res-year-stage-title">{{ stage.label }}</h3>
              <div class="res-year-grid">
                <button
                  type="button"
                  class="res-year-card"
                  v-for="year in stage.years"
                  :key="year.id"
                  @click="selectYear(year)"
                >
                  {{ year.name }}
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- ══ Step 2: form card ══ -->
      <div class="res-card" v-else>

        <div class="res-selected-year-banner">
          <span>السنة الدراسية المختارة: <strong>{{ selectedYear?.name }}</strong></span>
          <button type="button" class="res-change-year-btn" @click="backToYearSelection">
            تغيير السنة الدراسية
          </button>
        </div>

        <div v-if="loadingSettings" class="res-loading">
          <span class="res-spinner res-spinner--dark"></span>
        </div>

        <form v-else-if="formSettings" @submit.prevent="sendForm" novalidate>

          <!-- ── Section 1: Personal info ── -->
          <div class="res-section">
            <div class="res-section-head">
              <div class="res-section-dot"></div>
              <span>البيانات الشخصية</span>
            </div>
            <div class="res-grid res-grid--2">

              <!-- Student name -->
              <div class="rf-field" :class="{ 'rf-field--error': v$.student_name.$error }">
                <label class="rf-label">اسم الطالب <span class="rf-req">*</span></label>
                <div class="rf-input-wrap">
                  <span class="rf-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  </span>
                  <input type="text" v-model="student_name" class="rf-input" placeholder="الاسم ثلاثي عربي" @input="v$.student_name.$touch()" />
                </div>
                <span class="rf-error" v-if="v$.student_name.$error">
                  <template v-if="v$.student_name.required.$invalid">برجاء إدخال اسم الطالب</template>
                  <template v-else-if="v$.student_name.arabicOnly.$invalid">يرجى إدخال اسم الطالب باللغة العربية فقط.</template>
                  <template v-else>يجب ألا يقل اسم الطالب عن 10 أحرف</template>
                </span>
              </div>

              <!-- Gender -->
              <div class="rf-field" :class="{ 'rf-field--error': v$.selected_gender.$error }">
                <label class="rf-label">الجنس <span class="rf-req">*</span></label>
                <div class="rf-pill-group">
                  <button type="button" v-for="g in genders" :key="g.id"
                    class="rf-pill" :class="{ 'rf-pill--active': selected_gender?.id === g.id }"
                    @click="selected_gender = g">
                    {{ g.id === 1 ? '👦' : '👧' }} {{ g.name }}
                  </button>
                </div>
                <span class="rf-error" v-if="v$.selected_gender.$error">برجاء اختيار الجنس</span>
              </div>

              <!-- Student type (new/existing) -->
              <div class="rf-field" v-if="formSettings.student_type.visible" :class="{ 'rf-field--error': v$.selected_student_type.$error }">
                <label class="rf-label">
                  {{ formSettings.student_type.label }} <span class="rf-req" v-if="formSettings.student_type.required">*</span>
                </label>
                <div class="rf-pill-group">
                  <button type="button" v-for="t in studentTypes" :key="t.id"
                    class="rf-pill" :class="{ 'rf-pill--active': selected_student_type?.id === t.id }"
                    @click="selected_student_type = t">
                    {{ t.name }}
                  </button>
                </div>
                <span class="rf-error" v-if="v$.selected_student_type.$error">{{ formSettings.student_type.error_required }}</span>
              </div>

            </div>
          </div>

          <!-- ── Section 2: Phone numbers ── -->
          <div class="res-section">
            <div class="res-section-head">
              <div class="res-section-dot"></div>
              <span>أرقام التواصل</span>
            </div>
            <div class="res-grid res-grid--2">

              <!-- Student phone -->
              <div class="rf-field" :class="{ 'rf-field--error': v$.student_phone.$error }">
                <label class="rf-label">
                  رقم هاتف الطالب <span class="rf-req">*</span>
                </label>
                <span class="rf-hint">(أو رقم ولي الأمر إن لم يتوافر)</span>
                <div class="rf-input-wrap">
                  <span class="rf-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 11 19.79 19.79 0 01.22 2.38 2 2 0 012.2.22h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.1 6.1l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                  </span>
                  <input type="tel" v-model="student_phone" class="rf-input" placeholder="01XXXXXXXXX" dir="ltr" @keypress="onlyNumbers" @input="v$.student_phone.$touch()" inputmode="tel" />
                </div>
                <span class="rf-error" v-if="v$.student_phone.$error">
                  <template v-if="v$.student_phone.required.$invalid">برجاء إدخال رقم هاتف الطالب</template>
                  <template v-else-if="v$.student_phone.exact11.$invalid">رقم الهاتف يجب أن يتكون من 11 رقم.</template>
                  <template v-else>يجب أن يبدأ الرقم بـ 01.</template>
                </span>
              </div>

              <!-- Parent phone -->
              <div class="rf-field" :class="{ 'rf-field--error': v$.parent_phone.$error }">
                <label class="rf-label">رقم هاتف ولي الأمر</label>
                <span class="rf-hint">في حالة عدم توفر رقم ولي الأمر، يمكن إدخال رقم الهاتف الأرضي.</span>
                <div class="rf-input-wrap">
                  <span class="rf-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 11 19.79 19.79 0 01.22 2.38 2 2 0 012.2.22h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.1 6.1l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                  </span>
                  <input type="tel" v-model="parent_phone" class="rf-input" placeholder="01XXXXXXXXX" dir="ltr" @keypress="onlyNumbers" @input="v$.parent_phone.$touch()" inputmode="tel" />
                </div>
                <span class="rf-error" v-if="v$.parent_phone.$error">
                  رقم ولي الأمر يجب أن يتكون من 7 إلى 11 رقم.
                </span>
              </div>

              <!-- WhatsApp number -->
              <div class="rf-field" v-if="formSettings.whatsapp_number.visible" :class="{ 'rf-field--error': v$.whatsapp_number.$error }">
                <label class="rf-label">
                  {{ formSettings.whatsapp_number.label }} <span class="rf-req" v-if="formSettings.whatsapp_number.required">*</span>
                </label>
                <div class="rf-input-wrap">
                  <span class="rf-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 11 19.79 19.79 0 01.22 2.38 2 2 0 012.2.22h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.1 6.1l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                  </span>
                  <input type="tel" v-model="whatsapp_number" class="rf-input" :placeholder="formSettings.whatsapp_number.placeholder" dir="ltr" @keypress="onlyNumbers" @input="v$.whatsapp_number.$touch()" inputmode="tel" />
                </div>
                <span class="rf-error" v-if="v$.whatsapp_number.$error">
                  <template v-if="v$.whatsapp_number.required?.$invalid">{{ formSettings.whatsapp_number.error_required }}</template>
                  <template v-else-if="v$.whatsapp_number.exact11.$invalid">{{ formSettings.whatsapp_number.error_digits }}</template>
                  <template v-else>{{ formSettings.whatsapp_number.error_prefix }}</template>
                </span>
              </div>

            </div>
          </div>

          <!-- ── Section 3: Academic info ── -->
          <div class="res-section">
            <div class="res-section-head">
              <div class="res-section-dot"></div>
              <span>البيانات التعليمية</span>
            </div>
            <div class="res-grid res-grid--pair">

              <!-- Address -->
              <div class="rf-field" v-if="formSettings.address.visible" :class="{ 'rf-field--error': v$.address.$error }">
                <label class="rf-label">
                  {{ formSettings.address.label }} <span class="rf-req" v-if="formSettings.address.required">*</span>
                </label>
                <div class="rf-input-wrap">
                  <span class="rf-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  </span>
                  <input type="text" v-model="address" class="rf-input" :placeholder="formSettings.address.placeholder" />
                </div>
                <span class="rf-error" v-if="v$.address.$error">{{ formSettings.address.error_required }}</span>
              </div>

              <!-- Education type -->
              <div class="rf-field" :class="{ 'rf-field--error': v$.selected_type.$error }">
                <label class="rf-label">نظام التعليم <span class="rf-req">*</span></label>
                <div class="rf-pill-group">
                  <button type="button" v-for="t in types" :key="t.id"
                    class="rf-pill" :class="{ 'rf-pill--active': selected_type?.id === t.id }"
                    @click="selected_type = t">
                    {{ t.name }}
                  </button>
                </div>
                <span class="rf-error" v-if="v$.selected_type.$error">برجاء اختيار نظام التعليم</span>
              </div>

            </div>

            <div class="res-grid res-grid--pair">

              <!-- Student actual grade -->
              <div class="rf-field" v-if="formSettings.student_actual_grade.visible" :class="{ 'rf-field--error': v$.student_actual_grade.$error }">
                <label class="rf-label">
                  {{ formSettings.student_actual_grade.label }} <span class="rf-req" v-if="formSettings.student_actual_grade.required">*</span>
                </label>
                <div class="rf-input-wrap">
                  <span class="rf-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
                  </span>
                  <input type="text" v-model="student_actual_grade" class="rf-input" :placeholder="formSettings.student_actual_grade.placeholder" dir="ltr" @keypress="onlyNumbers" @input="v$.student_actual_grade.$touch()" inputmode="numeric" />
                </div>
                <span class="rf-error" v-if="v$.student_actual_grade.$error">
                  <template v-if="v$.student_actual_grade.required?.$invalid">{{ formSettings.student_actual_grade.error_required }}</template>
                  <template v-else-if="v$.student_actual_grade.numeric.$invalid">{{ formSettings.student_actual_grade.error_invalid }}</template>
                  <template v-else-if="v$.student_actual_grade.minValue.$invalid">{{ formSettings.student_actual_grade.error_min }}</template>
                  <template v-else-if="v$.student_actual_grade.notGreaterThanMaximum.$invalid">{{ formSettings.student_actual_grade.error_range }}</template>
                </span>
              </div>

              <!-- Maximum grade -->
              <div class="rf-field" v-if="formSettings.maximum_grade.visible" :class="{ 'rf-field--error': v$.maximum_grade.$error }">
                <label class="rf-label">
                  {{ formSettings.maximum_grade.label }} <span class="rf-req" v-if="formSettings.maximum_grade.required">*</span>
                </label>
                <div class="rf-input-wrap">
                  <span class="rf-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
                  </span>
                  <input type="text" v-model="maximum_grade" class="rf-input" :placeholder="formSettings.maximum_grade.placeholder" dir="ltr" @keypress="onlyNumbers" @input="v$.maximum_grade.$touch()" inputmode="numeric" />
                </div>
                <span class="rf-error" v-if="v$.maximum_grade.$error">
                  <template v-if="v$.maximum_grade.required?.$invalid">{{ formSettings.maximum_grade.error_required }}</template>
                  <template v-else-if="v$.maximum_grade.numeric.$invalid">{{ formSettings.maximum_grade.error_invalid }}</template>
                  <template v-else-if="v$.maximum_grade.minValue.$invalid">{{ formSettings.maximum_grade.error_min }}</template>
                  <template v-else-if="v$.maximum_grade.maxValue.$invalid">{{ formSettings.maximum_grade.error_max }}</template>
                </span>
              </div>

            </div>
          </div>

          <!-- ── Section 4: Certificate image ── -->
          <div class="res-section" v-if="formSettings.certificate_image.visible">
            <div class="res-section-head">
              <div class="res-section-dot"></div>
              <span>صورة الشهادة</span>
            </div>

            <!-- Upload area -->
            <label for="certificateImage" class="res-upload" :class="{ 'res-upload--has-file': imageEntry, 'res-upload--error': v$.fileData.$error }">
              <!-- Preview -->
              <img v-if="imageEntry" :src="imageEntry" class="res-upload-preview" alt="صورة الشهادة" />

              <!-- Empty state -->
              <div v-else class="res-upload-empty">
                <div class="res-upload-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                </div>
                <p class="res-upload-text">
                  {{ formSettings.certificate_image.upload_text }} <span v-if="formSettings.certificate_image.required" class="rf-req">*</span>
                </p>
                <p class="res-upload-hint">{{ formSettings.certificate_image.upload_hint }}</p>
              </div>

              <!-- File name badge -->
              <div v-if="imageEntry" class="res-upload-name">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M13.25 4.25l-3.5-3.5H3.75A1.5 1.5 0 002.25 2.25v11.5a1.5 1.5 0 001.5 1.5h8.5a1.5 1.5 0 001.5-1.5v-9.5z"/></svg>
                {{ fileData?.name }}
              </div>
            </label>
            <input type="file" id="certificateImage" @change="onFileChange" hidden accept="image/png, image/gif, image/jpeg, image/jpg" />

            <div class="res-upload-note">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" class="res-note-icon"><circle cx="8" cy="8" r="6.5"/><path d="M8 5v4M8 10.5v.5"/></svg>
              برجاء احضار الشهادة الأصلية أول حصة لدخول الدرس
            </div>
            <span class="rf-error" v-if="v$.fileData.$error">{{ formSettings.certificate_image.error_required }}</span>
          </div>

          <!-- ── Contact info ── -->
          <div class="res-contact-banner">
            <div class="res-contact-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <div class="res-contact-body">
              <p class="res-contact-label">واجهتك مشكلة في الحجز؟ تواصل معنا عبر الواتساب</p>
              <div class="res-contact-links">
                <a href="https://wa.me/+201111488849" target="_blank" class="res-wa-link">01111488849</a>
                <span class="res-contact-or">أو</span>
                <a href="https://wa.me/+201507306734" target="_blank" class="res-wa-link">01507306734</a>
              </div>
            </div>
          </div>

          <!-- ── Submit ── -->
          <div class="res-submit-row">
            <button type="submit" class="res-submit" :disabled="formSending">
              <span v-if="!formSending">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="width:18px;height:18px;vertical-align:middle;margin-left:6px"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke-linecap="round" stroke-linejoin="round"/></svg>
                إرسال طلب الحجز
              </span>
              <span v-else class="res-spinner"></span>
            </button>
          </div>

        </form>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { websiteTexts } from '@/websiteTexts';
import { scrollToFirstError } from '@/composables/useScrollToFirstError';
import {
  arabicOnly,
  digitsBetween,
  exactDigits,
  isNumericOrEmpty,
  maxValueOrEmpty,
  minValueOrEmpty,
  notGreaterThan,
  startsWithZeroOne,
  toEnglishDigits,
  trimmedMinLength,
} from '@/composables/useRegistrationValidators';

const STAGE_LABELS = {
  primary: 'المرحلة الابتدائية',
  prep: 'المرحلة الإعدادية',
  secondary: 'المرحلة الثانوية',
};
const STAGE_ORDER = ['primary', 'prep', 'secondary'];

export default {
  name: "reservation-page",
  data() {
    return {
      v$: useVuelidate(),

      // Step control
      step: 1,
      loadingYears: false,
      academicYears: [],
      selectedYear: null,

      // Server-authoritative "now", captured alongside academicYears so the
      // reservation_start_at gate isn't fooled by a skewed client clock.
      serverTimeAtFetch: null,
      serverTimeFetchedAt: null,

      // Dynamic form settings for the selected academic year
      loadingSettings: false,
      formSettings: null,

      // Static fields
      student_name: "",
      student_phone: null,
      parent_phone: null,
      selected_gender: null,
      selected_type: null,

      // Dynamic fields (configurable per academic year)
      address: "",
      whatsapp_number: null,
      student_actual_grade: null,
      maximum_grade: null,
      selected_student_type: null,
      certificate_image: "",
      imageEntry: "",
      fileData: "",

      formSending: false,
      genders: [
        { name: 'ولد', id: 1 },
        { name: 'بنت', id: 2 },
      ],
      types: [
        { name: 'عام', id: 1 },
        { name: 'أزهر', id: 2 },
      ],
      studentTypes: [
        { name: 'مستجد', id: 1 },
        { name: 'قديم', id: 2 },
      ],
    };
  },
  computed: {
    websiteTexts() { return websiteTexts; },
    stagesWithYears() {
      return STAGE_ORDER
        .map((key) => ({
          key,
          label: STAGE_LABELS[key],
          years: this.academicYears.filter((y) => y.stage === key),
        }))
        .filter((stage) => stage.years.length);
    },
  },
  validations() {
    const fs = this.formSettings;
    return {
      student_name:          { required, arabicOnly, minLength10: trimmedMinLength(10) },
      student_phone:         { required, exact11: exactDigits(11), startsWith01: startsWithZeroOne },
      parent_phone:          { digitsBetween: digitsBetween(7, 11) },
      selected_gender:       { required },
      selected_type:         { required },
      address:                fs?.address?.required ? { required } : {},
      fileData:               fs?.certificate_image?.required ? { required } : {},
      whatsapp_number:        {
        ...(fs?.whatsapp_number?.required ? { required } : {}),
        exact11: exactDigits(fs?.whatsapp_number?.validation?.length ?? 11),
        startsWith01: startsWithZeroOne,
      },
      student_actual_grade:   {
        ...(fs?.student_actual_grade?.required ? { required } : {}),
        numeric: isNumericOrEmpty,
        minValue: minValueOrEmpty(0),
        notGreaterThanMaximum: notGreaterThan(() => this.maximum_grade),
      },
      maximum_grade:          {
        ...(fs?.maximum_grade?.required ? { required } : {}),
        numeric: isNumericOrEmpty,
        minValue: minValueOrEmpty(1),
        maxValue: maxValueOrEmpty(120),
      },
      selected_student_type:  fs?.student_type?.required ? { required } : {},
    };
  },
  methods: {
    async fetchAcademicYears() {
      this.loadingYears = true;
      try {
        const { data } = await axios.get('/academic-years/reservation-available');
        this.academicYears = data.data || [];
        this.serverTimeAtFetch = new Date(String(data.server_time).replace(' ', 'T'));
        this.serverTimeFetchedAt = Date.now();
      } catch {
        this.academicYears = [];
      } finally {
        this.loadingYears = false;
      }
    },
    // Server time advanced by however long has elapsed on the client since fetch,
    // so the gate stays accurate without needing a live ticking countdown here.
    currentServerTime() {
      if (!this.serverTimeAtFetch) return new Date();
      return new Date(this.serverTimeAtFetch.getTime() + (Date.now() - this.serverTimeFetchedAt));
    },
    formatArabicDate(date) {
      const days = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      return `${days[date.getDay()]} ${dd}/${mm}/${date.getFullYear()}`;
    },
    formatArabicTime(date) {
      let h = date.getHours();
      const m = String(date.getMinutes()).padStart(2, '0');
      const period = h >= 12 ? 'مساءً' : 'صباحًا';
      h = h % 12 || 12;
      return `${h}:${m} ${period}`;
    },
    // Mirrors the backend guard in RegisterWebApiController::register() so the
    // form is never even shown before it would be rejected on submit.
    canEnterReservation(year) {
      if (!year.is_reservation_open) {
        Swal.fire({
          position: "center",
          icon: "info",
          title: "الحجز غير متاح لهذه السنة الدراسية حالياً",
          confirmButtonText: "حسنًا",
        });
        return false;
      }
      if (!year.reservation_start_at) {
        return true;
      }
      const startAt = new Date(String(year.reservation_start_at).replace(' ', 'T'));
      if (this.currentServerTime() < startAt) {
        Swal.fire({
          position: "center",
          icon: "info",
          title: "الحجز لم يبدأ بعد",
          text: `سيبدأ الحجز لهذه السنة يوم ${this.formatArabicDate(startAt)} الساعة ${this.formatArabicTime(startAt)}`,
          confirmButtonText: "حسنًا",
        });
        return false;
      }
      return true;
    },
    async selectYear(year) {
      if (!this.canEnterReservation(year)) return;
      this.selectedYear = year;
      this.step = 2;
      this.loadingSettings = true;
      this.formSettings = null;
      try {
        const { data } = await axios.get(`/academic-years/${year.id}/registration-settings`);
        this.formSettings = data.form_settings;
      } catch {
        Swal.fire({ position: "center", icon: "error", title: "تعذر تحميل بيانات نموذج التسجيل، برجاء المحاولة مرة أخرى" });
        this.step = 1;
      } finally {
        this.loadingSettings = false;
      }
    },
    backToYearSelection() {
      this.step = 1;
      this.selectedYear = null;
      this.formSettings = null;
    },
    onFileChange(event) {
      this.fileData = event.target.files[0];
      const reader = new FileReader();
      reader.readAsDataURL(this.fileData);
      reader.onload = () => (this.imageEntry = reader.result);
    },
    onlyNumbers(event) {
      const char = String.fromCharCode(event.which);
      if (!/[0-9٠-٩]/.test(char)) { event.preventDefault(); return; }
      const value = event.target.value;
      const digitsCount = (value.match(/[0-9٠-٩]/g) || []).length;
      if (digitsCount >= 11) event.preventDefault();
    },
    onlyDigits(event) {
      const char = String.fromCharCode(event.which);
      if (!/[0-9٠-٩]/.test(char)) event.preventDefault();
    },
    toEnglishDigits(str) {
      return toEnglishDigits(str);
    },
    sendForm() {
      this.v$.$validate();
      if (this.v$.$error) {
        // Error spans/classes are rendered off v$ reactively; wait for that DOM
        // update to land before searching for the first one to scroll to.
        this.$nextTick(() => scrollToFirstError());
        return;
      }
      if (!this.v$.$error) {
        const formData = new FormData();
        formData.append('student_name', this.student_name.trim());
        formData.append('student_phone', this.toEnglishDigits(this.student_phone));
        if (this.parent_phone) {
          formData.append('parent_phone', this.toEnglishDigits(this.parent_phone));
        }
        formData.append('gender', this.selected_gender.id);
        formData.append('education_type', this.selected_type.id);
        formData.append('academic_year_id', this.selectedYear.id);

        if (this.formSettings.address.visible && this.address) {
          formData.append('address', this.address);
        }
        if (this.formSettings.whatsapp_number.visible && this.whatsapp_number) {
          formData.append('whatsapp_number', this.toEnglishDigits(this.whatsapp_number));
        }
        if (this.formSettings.student_actual_grade.visible && this.student_actual_grade) {
          formData.append('student_actual_grade', this.toEnglishDigits(String(this.student_actual_grade).replace(/,/g, '.')));
        }
        if (this.formSettings.maximum_grade.visible && this.maximum_grade) {
          formData.append('maximum_grade', this.toEnglishDigits(String(this.maximum_grade).replace(/,/g, '.')));
        }
        if (this.formSettings.student_type.visible && this.selected_student_type) {
          formData.append('student_type', this.selected_student_type.id);
        }
        if (this.formSettings.certificate_image.visible && this.fileData instanceof File) {
          formData.append("certificate_image", this.fileData);
        }

        this.formSending = true;

        axios.post('/register', formData)
          .then((response) => {
            this.formSending = false;
            this.resetForm();
            Swal.fire({
              position: "center",
              icon: "success",
              title: " رقم الحجز الخاص بك " + String(response.data.data.reservation_number),
              html: (response.data.data?.after_submit_message || websiteTexts.data.success_form_book || '').replace(/&nbsp;/g, ' '),
            });
          })
          .catch((error) => {
            this.formSending = false;
            Swal.fire({ position: "center", icon: "error", title: error.response?.data?.message || "حدث خطأ أثناء إرسال البيانات" });
          });
      }
    },
    resetForm() {
      this.v$.$reset();
      this.student_name = "";
      this.student_phone = null;
      this.parent_phone = null;
      this.address = "";
      this.whatsapp_number = null;
      this.student_actual_grade = null;
      this.maximum_grade = null;
      this.certificate_image = "";
      this.imageEntry = "";
      this.fileData = "";
      this.selected_gender = null;
      this.selected_type = null;
      this.selected_student_type = null;
      this.step = 1;
      this.selectedYear = null;
      this.formSettings = null;
    },
  },
  mounted() {
    this.fetchAcademicYears();
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.res-page {
  min-height: calc(100vh - 70px);
  background: #F5F7FB;
  font-family: 'Cairo', sans-serif;
  direction: rtl;
  padding-bottom: 4rem;
}

/* Top accent strip */
.res-top-strip {
  height: 4px;
  background: linear-gradient(90deg, #5B5FEF, #7C4DFF, #00C2FF);
}

.res-container { max-width: 900px; margin: 0 auto; padding: 0 1.25rem; }

/* ── Header ── */
.res-header { text-align: center; padding: 2.5rem 1rem 1.75rem; }
.res-header-icon {
  width: 72px; height: 72px;
  background: linear-gradient(135deg, #5B5FEF, #7C4DFF);
  border-radius: 22px;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 1rem;
  box-shadow: 0 16px 40px rgba(91,95,239,0.3);
}
.res-header-icon svg { width: 34px; height: 34px; stroke: #fff; }
.res-header-title {
  font-size: 1.9rem; font-weight: 800; color: #1A1A1A; margin-bottom: 0.4rem;
}
.res-header-sub { font-size: 0.92rem; color: #6B7280; }

/* ── Card ── */
.res-card {
  background: rgba(255,255,255,0.97);
  border-radius: 28px;
  padding: 2.25rem 2rem;
  box-shadow: 0 20px 60px rgba(91,95,239,0.1), 0 4px 16px rgba(0,0,0,0.06);
  border: 1px solid rgba(255,255,255,0.9);
  display: flex; flex-direction: column; gap: 0;
}

/* ── Loading / empty states ── */
.res-loading { display: flex; justify-content: center; padding: 2.5rem 0; }
.res-spinner--dark {
  border: 3px solid rgba(91,95,239,0.18);
  border-top-color: #5B5FEF;
}
.res-empty {
  text-align: center; color: #6B7280; font-size: 0.92rem; padding: 2rem 1rem;
}

/* ── Step 1: academic year selection ── */
.res-year-stage { margin-bottom: 1.5rem; }
.res-year-stage:last-child { margin-bottom: 0; }
.res-year-stage-title {
  font-size: 0.95rem; font-weight: 800; color: #5B5FEF;
  margin-bottom: 0.75rem;
}
.res-year-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 0.75rem;
}
.res-year-card {
  padding: 1rem 0.75rem;
  border: 2px solid #E5E7EB; border-radius: 14px;
  background: #F9FAFB;
  font-family: 'Cairo', sans-serif;
  font-size: 0.92rem; font-weight: 700; color: #374151;
  cursor: pointer; transition: all 0.22s;
  text-align: center;
}
.res-year-card:hover {
  border-color: #5B5FEF; color: #5B5FEF;
  background: rgba(91,95,239,0.05);
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(91,95,239,0.15);
}

/* ── Selected year banner ── */
.res-selected-year-banner {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 0.75rem;
  background: rgba(91,95,239,0.06);
  border: 1px solid rgba(91,95,239,0.15);
  border-radius: 14px;
  padding: 0.85rem 1.1rem;
  margin-bottom: 1.5rem;
  font-size: 0.9rem; color: #374151;
}
.res-change-year-btn {
  border: none; background: none;
  color: #5B5FEF; font-weight: 700; font-size: 0.85rem;
  cursor: pointer; text-decoration: underline;
  font-family: 'Cairo', sans-serif;
}

/* ── Section ── */
.res-section { padding: 1.5rem 0; border-bottom: 1px solid rgba(91,95,239,0.07); }
.res-section:last-of-type { border-bottom: none; }

.res-section-head {
  display: flex; align-items: center; gap: 0.6rem;
  font-size: 0.88rem; font-weight: 800;
  color: #5B5FEF; margin-bottom: 1.1rem;
  letter-spacing: 0.02em;
}
.res-section-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: linear-gradient(135deg, #5B5FEF, #7C4DFF); flex-shrink: 0;
}

/* ── Grids ── */
.res-grid { display: grid; gap: 1.1rem; }
.res-grid--2 { grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); }
.res-grid--3 { grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); }

/* Paired rows (e.g. Address/Education type, Student grade/Final grade):
   always 2 columns when both fields are present, and the remaining field
   expands to full width when its sibling is hidden by the form config. */
.res-grid--pair { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.res-grid--pair + .res-grid--pair { margin-top: 1.1rem; }
.res-grid--pair .rf-field:only-child { grid-column: 1 / -1; }

/* ── Field ── */
.rf-field { display: flex; flex-direction: column; gap: 0.35rem; }
.rf-label {
  font-size: 0.83rem; font-weight: 700; color: #374151;
  display: flex; align-items: center; gap: 0.3rem; flex-wrap: wrap;
}
.rf-req { color: #EF4444; }
.rf-hint { font-size: 0.72rem; color: #9CA3AF; font-weight: 500; }

.rf-input-wrap { position: relative; display: flex; align-items: center; }
.rf-icon {
  position: absolute; right: 12px;
  width: 18px; height: 18px;
  color: #9CA3AF; pointer-events: none;
  display: flex; align-items: center; justify-content: center;
}
.rf-icon svg { width: 16px; height: 16px; }

.rf-input {
  width: 100%;
  padding: 0.78rem 2.5rem 0.78rem 1rem;
  border: 2px solid #E5E7EB;
  border-radius: 13px;
  font-family: 'Cairo', sans-serif;
  font-size: 0.92rem; color: #1A1A1A;
  background: #F9FAFB;
  outline: none;
  transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
}
.rf-input:focus {
  border-color: #5B5FEF; background: #fff;
  box-shadow: 0 0 0 4px rgba(91,95,239,0.1);
}
.rf-field--error .rf-input { border-color: #EF4444; box-shadow: 0 0 0 4px rgba(239,68,68,0.08); }
.rf-field--error .rf-icon { color: #EF4444; }
.rf-error { font-size: 0.9rem; color: #EF4444; font-weight: 600; }

/* ── Pill group ── */
.rf-pill-group { display: flex; gap: 0.6rem; flex-wrap: wrap; }
.rf-pill {
  padding: 0.6rem 1.5rem;
  border: 2px solid #E5E7EB; border-radius: 99px;
  background: #F9FAFB;
  font-family: 'Cairo', sans-serif;
  font-size: 0.9rem; font-weight: 700; color: #6B7280;
  cursor: pointer; transition: all 0.22s;
}
.rf-pill:hover { border-color: #5B5FEF; color: #5B5FEF; background: rgba(91,95,239,0.05); }
.rf-pill--active {
  background: linear-gradient(135deg, #5B5FEF, #7C4DFF);
  border-color: transparent; color: #fff;
  box-shadow: 0 8px 20px rgba(91,95,239,0.28);
}

/* ── Upload ── */
.res-upload {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 0.75rem;
  border: 2.5px dashed #D1D5DB;
  border-radius: 18px;
  padding: 2rem 1.5rem;
  cursor: pointer;
  transition: all 0.25s;
  background: #F9FAFB;
  min-height: 160px;
  position: relative; overflow: hidden;
}
.res-upload:hover { border-color: #5B5FEF; background: rgba(91,95,239,0.03); }
.res-upload--has-file { border-style: solid; border-color: #22C55E; background: rgba(34,197,94,0.04); }
.res-upload--error { border-color: #EF4444; }

.res-upload-preview {
  max-height: 180px; max-width: 100%;
  object-fit: contain; border-radius: 10px;
}
.res-upload-empty { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }
.res-upload-icon {
  width: 52px; height: 52px;
  background: rgba(91,95,239,0.08);
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  color: #5B5FEF;
}
.res-upload-icon svg { width: 26px; height: 26px; }
.res-upload-text { font-size: 0.92rem; font-weight: 700; color: #374151; }
.res-upload-hint { font-size: 0.76rem; color: #9CA3AF; }

.res-upload-name {
  display: flex; align-items: center; gap: 0.4rem;
  background: rgba(34,197,94,0.1);
  color: #16a34a; font-size: 0.8rem; font-weight: 700;
  padding: 0.35rem 0.85rem; border-radius: 99px;
}
.res-upload-name svg { width: 14px; height: 14px; stroke: #16a34a; }

.res-upload-note {
  display: flex; align-items: center; gap: 0.5rem;
  font-size: 1rem; color: #ff2828; font-weight: 600;
  background: rgba(239,91,91,0.06);
  border: 1px solid rgba(91,95,239,0.15);
  border-radius: 10px; padding: 0.55rem 0.85rem;
  margin-top: 0.6rem;
}
.res-note-icon { width: 15px; height: 15px; flex-shrink: 0; stroke: #ff2828; }

/* ── Contact banner ── */
.res-contact-banner {
  display: flex; align-items: center; gap: 1rem;
  background: linear-gradient(135deg, rgba(37,211,102,0.08), rgba(18,140,126,0.06));
  border: 1.5px solid rgba(37,211,102,0.2);
  border-radius: 16px; padding: 1rem 1.25rem;
  margin: 1.5rem 0 0;
}
.res-contact-icon {
  width: 44px; height: 44px; flex-shrink: 0;
  background: linear-gradient(135deg, #25D366, #128C7E);
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 8px 20px rgba(37,211,102,0.3);
}
.res-contact-icon svg { width: 24px; height: 24px; fill: #fff; }
.res-contact-body { flex: 1; }
.res-contact-label { font-size: 0.85rem; color: #374151; font-weight: 600; margin-bottom: 0.4rem; }
.res-contact-links { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; }
.res-wa-link {
  background: rgba(37,211,102,0.12); color: #16a34a;
  padding: 0.3rem 0.85rem; border-radius: 99px;
  font-size: 0.84rem; font-weight: 800; text-decoration: none;
  transition: all 0.2s;
}
.res-wa-link:hover { background: #25D366; color: #fff; }
.res-contact-or { font-size: 0.78rem; color: #9CA3AF; }

/* ── Submit ── */
.res-submit-row { display: flex; justify-content: center; padding-top: 1.75rem; }
.res-submit {
  padding: 1rem 3.5rem;
  border: none; border-radius: 99px;
  background: linear-gradient(135deg, #5B5FEF, #7C4DFF);
  color: #fff;
  font-family: 'Cairo', sans-serif;
  font-size: 1.05rem; font-weight: 800;
  cursor: pointer;
  box-shadow: 0 16px 40px rgba(91,95,239,0.35);
  transition: all 0.3s;
  display: flex; align-items: center; justify-content: center;
  min-height: 56px; min-width: 220px;
}
.res-submit:hover:not(:disabled) { transform: translateY(-4px); box-shadow: 0 24px 55px rgba(91,95,239,0.45); }
.res-submit:disabled { opacity: 0.75; cursor: not-allowed; }

.res-spinner {
  width: 24px; height: 24px;
  border: 3px solid rgba(255,255,255,0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: resSpin 0.8s linear infinite; display: inline-block;
}
@keyframes resSpin { to { transform: rotate(360deg); } }

@media (max-width: 768px) {
  .res-header { padding: 1.5rem 1rem 1.15rem; }
  .res-header-icon { width: 56px; height: 56px; border-radius: 17px; margin-bottom: .75rem; }
  .res-header-icon svg { width: 27px; height: 27px; }
  .res-header-title { font-size: 1.6rem; line-height: 1.3; margin-bottom: .3rem; }
  .res-header-sub { font-size: .85rem; line-height: 1.6; }
}

@media (max-width: 640px) {
  .res-card { padding: 1.5rem 1.1rem; border-radius: 20px; }
  .res-submit { width: 100%; padding: 1rem; }
  .res-contact-banner { flex-direction: column; text-align: center; }
  .res-contact-links { justify-content: center; }
  .res-selected-year-banner { flex-direction: column; align-items: flex-start; }
  .res-grid--pair { grid-template-columns: 1fr; }
}
</style>
